import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import AnimatedLine from '../components/AnimatedLine';
import { ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';

const Intelligence = () => {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const articles = [
    {
      title: "Attention Engineering: The Science of Making People Care",
      description: "The first 3 seconds determine 80% of your retention. Learn how to engineer content that captures and holds attention.",
      readTime: "10 min read",
      category: "Attention Science",
      slug: "attention-engineering"
    },
    {
      title: "Content Strategy in the Attention Economy",
      description: "Distribution is the strategy. Content is the tactic. Learn how to build a content system that compounds over time.",
      readTime: "12 min read",
      category: "Strategy",
      slug: "content-strategy"
    },
    {
      title: "Media Moats: The Unfair Advantage",
      description: "Owned media is the new competitive advantage. Learn how to build a media moat that drives consistent, compounding growth.",
      readTime: "14 min read",
      category: "Strategy",
      slug: "media-moats"
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: 'Newsletter Subscriber',
            email: email,
            message: 'Newsletter subscription request',
            source: 'intelligence_newsletter',
          }
        ]);

      if (error) throw error;

      setSubmitStatus({
        type: 'success',
        message: 'Successfully subscribed! Check your inbox for updates.'
      });
      
      setEmail('');
    } catch (error) {
      console.error('Error subscribing:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Failed to subscribe. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl sm:text-6xl md:text-7xl font-bold mb-8"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            State of Attention
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl sm:text-2xl text-white/70 mb-12"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            We publish what we learn. No gatekeeping.
          </motion.p>
        </div>
      </section>

      <AnimatedLine />

      {/* Featured Articles */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {articles.map((article, idx) => (
              <Link key={idx} to={`/intelligence/${article.slug}`}>
                <motion.article
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="border border-white/10 rounded-lg p-8 hover:border-white/30 transition-all group cursor-pointer"
                >
                  <div className="text-sm text-purple-400 mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {article.category}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 group-hover:text-purple-400 transition-colors" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {article.title}
                  </h3>
                  <p className="text-white/70 mb-6 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {article.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {article.readTime}
                    </span>
                    <div className="flex items-center gap-2 text-purple-400 group-hover:gap-4 transition-all">
                      <span className="text-sm font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>Read Analysis</span>
                      <ArrowRight size={16} />
                    </div>
                  </div>
                </motion.article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <AnimatedLine />

      {/* Lab Reports Section */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-bold mb-8 text-center"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            The Lab Reports
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl text-white/70 mb-12 text-center"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Real data from real campaigns. We screenshot our YouTube Studio analytics and explain what worked, what didn't, and why.
          </motion.p>

          {/* Example Lab Report */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="border border-white/10 rounded-lg p-8 bg-white/5"
          >
            <div className="mb-6">
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Why This Video Flopped
              </h3>
              <p className="text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                Published: Nov 28, 2024
              </p>
            </div>
            
            {/* Simulated Analytics Screenshot */}
            <div className="bg-black/50 rounded-lg p-6 mb-6 border border-white/5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-sm text-white/50 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>Average View Duration</div>
                  <div className="text-3xl font-bold text-red-400" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                    1:24
                  </div>
                </div>
                <div>
                  <div className="text-sm text-white/50 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>Retention Rate</div>
                  <div className="text-3xl font-bold text-red-400" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                    23%
                  </div>
                </div>
                <div>
                  <div className="text-sm text-white/50 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>CTR</div>
                  <div className="text-3xl font-bold text-red-400" style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                    2.1%
                  </div>
                </div>
              </div>
              <div className="h-32 bg-gradient-to-r from-purple-500/20 to-red-500/20 rounded"></div>
            </div>

            <div className="space-y-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              <p className="text-white/70">
                <span className="text-white font-semibold">The Problem:</span> We bored the audience at 0:05. The hook was too slow. We spent 8 seconds on a talking head before delivering value.
              </p>
              <p className="text-white/70">
                <span className="text-white font-semibold">The Fix:</span> Next iteration, we cut the intro entirely. Started with a pattern interrupt (visual + audio spike). Retention jumped to 58%.
              </p>
              <p className="text-white/70">
                <span className="text-white font-semibold">The Lesson:</span> Your audience doesn't owe you their attention. Earn it in the first frame, or lose them forever.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <AnimatedLine />

      {/* Newsletter Signup */}
      <section className="py-24 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="border border-white/10 rounded-lg p-12 text-center bg-white/5"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Join 5,000+ Founders
            </h2>
            <p className="text-white/70 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
              Receive the Weekly Lab Report. Real data, real insights, zero fluff.
            </p>
            
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-4 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400 transition-colors disabled:opacity-50"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
              
              {submitStatus.message && (
                <div
                  className={`p-4 rounded-lg text-center ${
                    submitStatus.type === 'success'
                      ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                      : 'bg-red-500/10 border border-red-500/20 text-red-400'
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {submitStatus.message}
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      <AnimatedLine />

      {/* Footer */}
      <footer className="py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/50 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
            © 2024 AttentionLabs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Intelligence;
